import type {
  MaterialCaptureUnit,
  MaterialPresentationType,
} from '../materials-catalog/index.js';

export const materialsPresentationSourceValues = ['station', 'company', 'catalog'] as const;
export type MaterialsPresentationSource = (typeof materialsPresentationSourceValues)[number];

export const materialsOrderingDefaultModeValues = [
  'published',
  'alpha_asc',
  'alpha_desc',
] as const;
export type MaterialsOrderingDefaultMode = (typeof materialsOrderingDefaultModeValues)[number];

export const materialOrderingRestoreBucketValues = ['pinned', 'visible'] as const;
export type MaterialOrderingRestoreBucket = (typeof materialOrderingRestoreBucketValues)[number];

export const materialCategoryRuleTypeValues = ['presentationTypes', 'unit', 'crvEligible'] as const;
export type MaterialCategoryRuleType = (typeof materialCategoryRuleTypeValues)[number];

/**
 * `presentationType` is optional so one ref can target either a single purchase card
 * or every presentation under a material.
 */
export interface MaterialCardRef {
  materialId: string;
  presentationType?: MaterialPresentationType | null;
}

export interface MaterialOrderingHiddenRef {
  ref: MaterialCardRef;
  restoreBucket: MaterialOrderingRestoreBucket;
}

export interface MaterialOrderingBuckets {
  pinned: MaterialCardRef[];
  visible: MaterialCardRef[];
  hidden: MaterialOrderingHiddenRef[];
}

/**
 * `version` is the persisted document schema version.
 * `revision` is the optimistic-concurrency token and changes on every successful replace.
 */
export interface MaterialOrderingConfigDocument {
  version: 1;
  companyId: string;
  stationId: string | null;
  revision: number;
  updatedAt: string;
  updatedByUserId: string | null;
  defaultMode: MaterialsOrderingDefaultMode;
  buckets: MaterialOrderingBuckets;
}

export interface GetMaterialOrderingConfigRequest {
  companyId: string;
  stationId?: string | null;
}

export interface GetMaterialOrderingConfigResponse {
  ordering: MaterialOrderingConfigDocument | null;
}

export interface ReplaceMaterialOrderingConfigRequest {
  companyId: string;
  stationId?: string | null;
  expectedRevision?: number | null;
  document: Omit<MaterialOrderingConfigDocument, 'companyId' | 'stationId' | 'revision' | 'updatedAt' | 'updatedByUserId'>;
}

export interface ReplaceMaterialOrderingConfigResponse {
  ordering: MaterialOrderingConfigDocument;
}

export interface ClearMaterialOrderingConfigRequest {
  companyId: string;
  stationId?: string | null;
  expectedRevision?: number | null;
}

export interface ClearMaterialOrderingConfigResponse {
  ordering: null;
}

export type MaterialCategoryRule =
  | {
      type: 'presentationTypes';
      presentationTypes: MaterialPresentationType[];
    }
  | {
      type: 'unit';
      unit: MaterialCaptureUnit;
    }
  | {
      type: 'crvEligible';
      isCrvEligible: boolean;
    };

export interface MaterialCategoryConfigEntry {
  id: string;
  name: string;
  isActive: boolean;
  rule: MaterialCategoryRule | null;
  members: MaterialCardRef[];
}

/**
 * Array order is authoritative category order.
 * `version` is the schema version, `revision` is the optimistic-concurrency token.
 */
export interface MaterialCategoryConfigDocument {
  version: 1;
  companyId: string;
  stationId: string | null;
  revision: number;
  updatedAt: string;
  updatedByUserId: string | null;
  categories: MaterialCategoryConfigEntry[];
}

export interface GetMaterialCategoryConfigRequest {
  companyId: string;
  stationId?: string | null;
}

export interface GetMaterialCategoryConfigResponse {
  categories: MaterialCategoryConfigDocument | null;
}

export interface ReplaceMaterialCategoryConfigRequest {
  companyId: string;
  stationId?: string | null;
  expectedRevision?: number | null;
  document: Omit<MaterialCategoryConfigDocument, 'companyId' | 'stationId' | 'revision' | 'updatedAt' | 'updatedByUserId'>;
}

export interface ReplaceMaterialCategoryConfigResponse {
  categories: MaterialCategoryConfigDocument;
}

export interface ClearMaterialCategoryConfigRequest {
  companyId: string;
  stationId?: string | null;
  expectedRevision?: number | null;
}

export interface ClearMaterialCategoryConfigResponse {
  categories: null;
}

export interface ResolveMaterialPresentationRequest {
  companyId: string;
  storeId?: string;
  locationId?: string;
  stationId?: string;
  effectiveOn?: string;
  includeInactive?: boolean;
}

export interface ResolvedMaterialCardRef {
  materialId: string;
  presentationType: MaterialPresentationType;
}

export interface ResolvedMaterialPresentationCard {
  ref: ResolvedMaterialCardRef;
  displayName: string;
  presentationLabel: string;
  categoryName: string | null;
  source: MaterialsPresentationSource;
}

export interface ResolvedMaterialPresentationGroup {
  id: string;
  name: string;
  cards: ResolvedMaterialPresentationCard[];
}

export interface ResolvedMaterialPresentationSourceInfo {
  source: MaterialsPresentationSource;
  revision: number | null;
}

export interface ResolvedMaterialPresentationWarning {
  code: 'legacy_category_overlap';
  message: string;
  details?: unknown;
}

export interface ResolveMaterialPresentationResponse {
  pinned: ResolvedMaterialPresentationCard[];
  groups: ResolvedMaterialPresentationGroup[];
  otherMaterials: ResolvedMaterialPresentationCard[];
  hiddenCount: number;
  orderingSource: ResolvedMaterialPresentationSourceInfo;
  categorySource: ResolvedMaterialPresentationSourceInfo;
  warnings: ResolvedMaterialPresentationWarning[];
}
